import React from "react";
import { useNavigate } from "react-router-dom";
import { useGetUserPurchaseHistoryQuery } from "../slices/usersApiSlice";
import { useSelector } from "react-redux";
import { useMemo, useState } from "react";
import Loader from "../components/Loader";
import Message from "../components/Message";
import { FaArrowRight, FaBoxOpen, FaChevronDown, FaChevronUp, FaFileInvoiceDollar, FaReceipt, FaTicketAlt } from "react-icons/fa";

const PurchaseHistoryScreen = () => {
    // Get the logged-in user's ID from Redux state
    const { userInfo } = useSelector((state) => state.auth);

    // Fetch the user's purchase history
    const { data: purchaseHistory, isLoading, error } = useGetUserPurchaseHistoryQuery(userInfo._id);
    const [sortBy, setSortBy] = useState("date-desc");
    const [openInvoiceLists, setOpenInvoiceLists] = useState({});

    const navigate = useNavigate()

    const sortedPurchases = useMemo(() => {
        const list = [...(purchaseHistory || [])];

        switch (sortBy) {
            case "date-asc":
                return list.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
            case "name-asc":
                return list.sort((a, b) => a.name.localeCompare(b.name));
            case "name-desc":
                return list.sort((a, b) => b.name.localeCompare(a.name));
            case "date-desc":
            default:
                return list.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        }
    }, [purchaseHistory, sortBy]);

    const purchaseStats = useMemo(() => {
      const list = purchaseHistory || [];
      return {
        totalPurchases: list.length,
        freePurchases: list.filter((entry) => Number(entry.purchasePrice || 0) <= 0).length,
        filesOwned: list.reduce((acc, entry) => acc + Number(entry.files?.length || 0), 0),
        licensesRemaining: list.reduce(
          (acc, entry) =>
            acc +
            (Array.isArray(entry.licenses)
              ? entry.licenses.reduce((sum, value) => sum + Number(value || 0), 0)
              : 0),
          0
        ),
      };
    }, [purchaseHistory]);

    const formatPurchaseDate = (value) =>
      new Date(value).toLocaleDateString("en-US", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });

    const openPurchase = (product) =>
      navigate("/purchases/purchased-product", {
        state: { userId: userInfo._id, productId: product.productId, purchaseId: product._id },
      });

    const toggleInvoiceList = (productId) => {
      setOpenInvoiceLists((prev) => ({
        ...prev,
        [String(productId)]: !prev[String(productId)],
      }));
    };

    return (
      <div className="space-y-4">


        <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
            <div>
              <h2 className="mb-1 text-2xl font-semibold text-slate-900">Purchase History</h2>
              <p className="m-0 text-sm text-slate-600">
                Review your owned resources, remaining licenses, and access each purchase from one place.
              </p>
            </div>
            <div className="w-full max-w-sm">
              <label htmlFor="purchaseSort" className="mb-1.5 block text-sm font-medium text-slate-700">
                Sort purchases
              </label>
              <select
                id="purchaseSort"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-800 outline-none transition focus:border-sky-300 focus:bg-white focus:ring-2 focus:ring-sky-100"
              >
                <option value="date-desc">Date: Recent to Oldest</option>
                <option value="date-asc">Date: Oldest to Recent</option>
                <option value="name-asc">Alphabetical: A to Z</option>
                <option value="name-desc">Alphabetical: Z to A</option>
              </select>
            </div>
          </div>
        </section>

        {isLoading ? (
          <Loader />
        ) : error ? (
          <Message variant="danger">{error?.data?.message || error.error}</Message>
        ) : (
          sortedPurchases.length > 0 ? (
            <section className="space-y-4">
              {sortedPurchases.map((product) => {
                const invoiceOrders = Array.isArray(product.invoiceOrders)
                  ? product.invoiceOrders
                  : [];
                const hasInvoices = invoiceOrders.length > 0;
                const invoiceListOpen = Boolean(openInvoiceLists[String(product.productId)]);

                return (
                <article
                  key={product._id || product.productId}
                  className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm"
                >
                  <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                    <div className="flex min-w-0 flex-1 flex-col gap-4 sm:flex-row">
                      <div className="h-24 w-24 shrink-0 overflow-hidden rounded-xl border border-slate-200 bg-slate-50">
                        {product.images?.[0] ? (
                          <img
                            src={product.images[0]}
                            alt={product.name}
                            className="h-full w-full object-cover"
                          />
                        ) : (
                          <div className="flex h-full w-full items-center justify-center text-xs font-semibold text-slate-400">
                            No image
                          </div>
                        )}
                      </div>

                      <div className="min-w-0 [overflow-wrap:anywhere]">
                        <div className="flex flex-wrap items-center gap-2">
                          <button
                            type="button"
                            className="min-w-0 max-w-full break-words text-left text-xl font-semibold text-slate-900 hover:text-sky-700"
                            onClick={() => openPurchase(product)}
                          >
                            {product.name}
                          </button>
                          <span className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-semibold text-slate-600">
                            {Number(product.purchasePrice || 0) <= 0 ? "Free resource" : "Paid resource"}
                          </span>
                          {["REFUNDED", "PARTIALLY_REFUNDED"].includes(product.refundStatus) && (
                            <span className="rounded-full border border-rose-200 bg-rose-50 px-2.5 py-1 text-xs font-semibold text-rose-700">
                              {product.refundStatus === "REFUNDED" ? "Refunded" : "Partially refunded"}
                            </span>
                          )}
                        </div>
                        <p className="mt-1 text-sm text-slate-600">
                          By <span className="font-medium text-slate-700">{product.ownerName}</span>
                        </p>
                        <div className="mt-2 flex flex-wrap gap-2 text-xs text-slate-500">
                          <span className="rounded-full bg-slate-100 px-2.5 py-1">
                            Purchased {formatPurchaseDate(product.createdAt)}
                          </span>
                          <span className="rounded-full bg-slate-100 px-2.5 py-1">
                            {Array.isArray(product.files) ? product.files.length : 0} file
                            {Array.isArray(product.files) && product.files.length === 1 ? "" : "s"}
                          </span>
                          <span className="rounded-full bg-slate-100 px-2.5 py-1">
                            {Number(product.purchasePrice || 0) <= 0
                              ? "Free"
                              : `$${Number(product.purchasePrice).toFixed(2)}`}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="flex flex-col items-start gap-2 lg:items-end">
                      <div className="flex flex-wrap gap-2 lg:justify-end">
                        <button
                          type="button"
                          onClick={() => openPurchase(product)}
                          className="inline-flex items-center gap-2 rounded-xl bg-sky-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-sky-700"
                        >
                          Open Resource
                          <FaArrowRight className="text-xs" />
                        </button>
                        {hasInvoices && (
                          <button
                            type="button"
                            onClick={() => toggleInvoiceList(product.productId)}
                            className="inline-flex items-center gap-2 rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
                          >
                            <FaFileInvoiceDollar className="text-sm" />
                            Invoices ({invoiceOrders.length})
                            {invoiceListOpen ? (
                              <FaChevronUp className="text-xs" />
                            ) : (
                              <FaChevronDown className="text-xs" />
                            )}
                          </button>
                        )}
                      </div>
                      <p className="m-0 break-all text-xs text-slate-500">{String(product.productId)}</p>
                    </div>
                  </div>

                  <div className="mt-4 border-t border-slate-100 pt-4">
                    <p className="mb-2 text-sm font-medium text-slate-700">Files and remaining licenses</p>
                    <p className="mb-3 text-xs leading-5 text-slate-500">
                      Remaining licenses show how many more times each file can still be downloaded
                      from this purchase.
                    </p>
                    {Array.isArray(product.files) && Array.isArray(product.licenses) ? (
                      <div className="grid grid-cols-1 gap-2 xl:grid-cols-2">
                        {product.files.map((file, idx) => {
                          const fileName = String(file || "").split("/").pop() || `File ${idx + 1}`;
                          const licenses = Number(product.licenses[idx] || 0);
                          return (
                            <div
                              key={`${product._id || product.productId}-file-${idx}`}
                              className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2"
                            >
                              <div className="min-w-0 [overflow-wrap:anywhere]">
                                <p className="m-0 truncate text-sm font-medium text-slate-800" title={fileName}>
                                  {fileName}
                                </p>
                              </div>
                              <span
                                className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-semibold ${
                                  licenses > 0
                                    ? "bg-emerald-100 text-emerald-700"
                                    : "bg-amber-100 text-amber-700"
                                }`}
                              >
                                {licenses} license{licenses === 1 ? "" : "s"}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="m-0 text-sm text-slate-500">No file/license details available.</p>
                    )}
                  </div>

                  {hasInvoices && invoiceListOpen && (
                    <div className="mt-4 border-t border-slate-100 pt-4">
                      <p className="mb-2 text-sm font-medium text-slate-700">Invoice history</p>
                      <div className="space-y-2">
                        {invoiceOrders.map((invoiceEntry) => (
                          <div
                            key={String(invoiceEntry.orderId)}
                            className="flex flex-col gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-3 sm:flex-row sm:items-center sm:justify-between"
                          >
                            <div className="text-sm text-slate-700">
                              <p className="m-0 font-semibold text-slate-900">{invoiceEntry.invoiceId}</p>
                              <p className="m-0 break-all text-xs text-slate-500">
                                Order date {formatPurchaseDate(invoiceEntry.createdAt)}
                                {invoiceEntry.paidAt ? ` • Paid ${formatPurchaseDate(invoiceEntry.paidAt)}` : ""}
                              </p>
                            </div>
                            <button
                              type="button"
                              onClick={() => navigate(`/order/${invoiceEntry.orderId}/invoice`)}
                              className="inline-flex items-center gap-2 rounded-xl bg-white px-4 py-2 text-sm font-semibold text-slate-700 ring-1 ring-slate-200 transition hover:bg-slate-100"
                            >
                              <FaFileInvoiceDollar className="text-sm" />
                              View Invoice
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </article>
              )})}
            </section>
          ) : (
            <section className="rounded-2xl border border-slate-200 bg-white p-8 text-center shadow-sm">
              <h3 className="text-xl font-semibold text-slate-900">No purchases found</h3>
              <p className="mt-2 text-sm text-slate-600">
                Once you buy or claim resources, they will appear here with their download licenses.
              </p>
            </section>
          )
        )}
        <section className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="flex items-center gap-3">
              <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-sky-100 text-sky-700">
                <FaBoxOpen />
              </span>
              <div>
                <p className="m-0 text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Purchases
                </p>
                <p className="m-0 text-2xl font-semibold text-slate-900">{purchaseStats.totalPurchases}</p>
              </div>
            </div>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="flex items-center gap-3">
              <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-100 text-emerald-700">
                <FaReceipt />
              </span>
              <div>
                <p className="m-0 text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Free Resources
                </p>
                <p className="m-0 text-2xl font-semibold text-slate-900">{purchaseStats.freePurchases}</p>
              </div>
            </div>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="flex items-center gap-3">
              <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-amber-100 text-amber-700">
                <FaTicketAlt />
              </span>
              <div>
                <p className="m-0 text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Licenses Left
                </p>
                <p className="m-0 text-2xl font-semibold text-slate-900">{purchaseStats.licensesRemaining}</p>
              </div>
            </div>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="flex items-center gap-3">
              <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-violet-100 text-violet-700">
                <FaBoxOpen />
              </span>
              <div>
                <p className="m-0 text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Files Owned
                </p>
                <p className="m-0 text-2xl font-semibold text-slate-900">{purchaseStats.filesOwned}</p>
              </div>
            </div>
          </div>
        </section>
      </div>
    );
};

export default PurchaseHistoryScreen;
